import React, { useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AGENTS_EDIT_ROUTE, EDIT_AGENT_QUERY_PARAM } from "../../../../../../../../../../../../agents.constants";
import { useEditAgentGuard } from "../../../../../../../../../../editAgentGuard.context";
import styles from "./agentHierarchyRow.module.css";
import { IAgentHierarchyInformation } from "../../../../../../../../../../../../agentDetail.model";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { AgentHierarchyStatusBadge } from "../agentHierarchyStatusBadge/agentHierarchyStatusBadge.index";


interface AgentHierarchyRowProps {
    node: IAgentHierarchyInformation;
    currentUserId: number;
    isRoot?: boolean;
}

export const AgentHierarchyRow = ({ node, currentUserId, isRoot = false }: AgentHierarchyRowProps) => {
    const [isOpen, setIsOpen] = useState(true);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const guardNavigation = useEditAgentGuard();

    const hasChildren = node.downLine.length > 0;
    const isCurrentUser = node.userId === currentUserId;
    const isNotClickable = isCurrentUser || isRoot;
    const fullName = node.agencyName?.trim() || `${node.firstName ?? ''} ${node.lastName ?? ''}`.trim() || "-";

    const metaParts = [
        node.email ? `Email: ${node.email}` : null,
        node.userType ? `Type: ${node.userType}` : null,
        node.agencyNumber ? `Agency Number: ${node.agencyNumber}` : null,
        node.writingNumber ? `Writing Number: ${node.writingNumber}` : null,
    ].filter(Boolean);

    const handleToggle = useCallback(() => {
        if (!hasChildren) return;
        setIsOpen((prev) => !prev);
    }, [hasChildren]);

    const editAgentLink = `${AGENTS_EDIT_ROUTE}?${EDIT_AGENT_QUERY_PARAM}=${node.userId}`;

    const handleNameClick = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            guardNavigation(() => {
                globalThis.open(editAgentLink, '_blank');
            });
        },
        [guardNavigation, node.userId]
    );

    return (
        <div ref={wrapperRef} className={`${styles.nodeWrapper} ${isRoot ? styles.root : ""}`}>
            <button
                className={`${styles.rowHeader} ${isCurrentUser ? styles.highlighted : ""} ${hasChildren ? styles.clickable : ""}`}
                onClick={handleToggle}
            >
                <div className={styles.iconCell}>
                    {hasChildren &&
                        (isOpen ? (
                            <KeyboardArrowUpIcon fontSize="small" className={styles.icon} />
                        ) : (
                            <KeyboardArrowDownIcon fontSize="small" className={styles.icon} />
                        ))}
                </div>
                <div className={styles.rowContent}>
                    <div className={styles.nameRow}>
                        <Link 
                            to={`${AGENTS_EDIT_ROUTE}?${EDIT_AGENT_QUERY_PARAM}=${node.userId}`}
                            target='_blank'
                            className={`${styles.agentName} ${isNotClickable ? styles.notClickable : ""}`}
                            title={`Open ${fullName}'s edit page`}
                            onClick={handleNameClick}
                        >
                            {fullName}
                        </Link>
                        <AgentHierarchyStatusBadge isActive={node.isActive} />
                    </div>
                    {metaParts.length > 0 && <div className={styles.metaRow}>{metaParts.join(" | ")}</div>}
                </div>
            </button>

            {isOpen && hasChildren && (
                <div className={styles.childrenContainer}>
                    {node.downLine.map((child) => (
                        <AgentHierarchyRow
                            key={child.userId}
                            node={child}
                            currentUserId={currentUserId}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};